import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PartyPopper, SaudiRiyal } from 'lucide-react'
import styled from 'styled-components'
import { usePaymentStore } from '../store/paymentStore'
import { useCartStore } from '../store/useCartStore'
import { eventHub } from '../lib/tracking/event-hub'
import { safeLocal } from '../lib/safeStorage'
import { FIRST_TOUCH_STORAGE_KEY } from '../lib/attributionFirstTouch'
import {
  deviceContextFromUnknown,
  loadPersistedPurchaseDeviceContext,
} from '../lib/tracking/deviceContext'
import * as S from './ResultStyles'

/** رقم الطلب فقط — لا نستخدم معرّف الشحن Tap (`id` / charge) في التتبع */
function resolveOrderNumberFromPaymentData(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const d = data as Record<string, unknown>
  const raw = d.order_number
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  const ref = d.reference
  if (ref && typeof ref === 'object') {
    const ro = (ref as Record<string, unknown>).order
    if (typeof ro === 'string' && ro.trim()) return ro.trim()
  }
  return ''
}

/** إعادة identify بعد العودة من بوابة الدفع (يجب أن يكون eh_user محفوظاً من صفحة الدفع) */
async function reidentifyFromCheckoutStorage(): Promise<void> {
  try {
    const raw = safeLocal.getItem('eh_user')
    if (!raw) return
    const u = JSON.parse(raw) as Record<string, unknown>
    const userId = u.user_id != null && u.user_id !== '' ? String(u.user_id) : undefined
    const email = typeof u.email === 'string' ? u.email : undefined
    const phone = typeof u.phone === 'string' ? u.phone : undefined
    if (!userId && !email && !phone) return

    await eventHub.identify({
      ...(userId ? { user_id: userId } : {}),
      first_name: typeof u.first_name === 'string' ? u.first_name : undefined,
      last_name: typeof u.last_name === 'string' ? u.last_name : undefined,
      email,
      phone,
      city: typeof u.city === 'string' ? u.city : undefined,
      state: typeof u.state === 'string' ? u.state : undefined,
      country: typeof u.country === 'string' ? u.country : 'SA',
      zip: typeof u.zip === 'string' ? u.zip : undefined,
    })
  } catch {
    // تجاهل أخطاء التخزين/JSON
  }
}

// يحجز إطلاق حدث الشراء على مستوى الخادم (dedup موثوق عبر الأجهزة).
// الحجز المحلي (localStorage) لا يكفي: فتح رابط النجاح من متصفح/جهاز آخر يبدأ بذاكرة
// فارغة فيُطلق الحدث من جديد. الخادم يحجز الطلب مرة واحدة ذرّياً.
// يعيد true إذا وجب الإطلاق: إمّا فاز هذا المتصفح بالحجز (claimed=true) أو تعذّر التأكد
// (fail-open حتى لا نفقد تحويلاً حقيقياً بسبب خطأ عابر). يعيد false فقط حين يؤكّد الخادم
// أن الحدث أُطلق مسبقاً لهذا الطلب.
async function claimPurchaseOnServer(chargeId: string): Promise<boolean> {
  if (!chargeId) return true // لا معرّف للحجز → أبقِ على سلوك dedup المحلي فقط
  try {
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com'
    const r = await fetch(
      `${BACKEND_URL}/api/public/purchase-claim/${encodeURIComponent(chargeId)}`,
      { method: 'POST' }
    )
    if (!r.ok) return true // استجابة غير متوقعة → fail-open
    const j = await r.json()
    if (j && j.success && j.data && typeof j.data.claimed === 'boolean') {
      return j.data.claimed // قرار الخادم النهائي: أطلق فقط إذا فزنا بالحجز
    }
    return true // شكل استجابة غير متوقع → fail-open
  } catch {
    return true // خطأ شبكة → fail-open
  }
}

// Card بدون ظل
const CardWithoutShadow = styled(S.Card)`
  box-shadow: none;
`

// Hero مع padding أقل في الأسفل
const HeroWithLessPadding = styled(S.Hero)`
  margin-bottom: 20px;
`

// تصميم بسيط وأنيق
const OrderNumberBox = styled.div`
  background: transparent;
  border: none;
  padding: 14px 0 8px 0;
  margin-bottom: 0;
  text-align: center;
`

const OrderNumberText = styled.div`
  color: oklch(55.44% 0.041 257.42);
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const OrderNumberValue = styled.div`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 20px;
  font-weight: 800;
  direction: ltr;
`

const ProductsContainer = styled.div`
  margin-bottom: 20px;
`

const ProductRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid oklch(0.9683 0.0069 247.9);
  
  &:last-child {
    border-bottom: none;
  }
`

const ProductInfo = styled.div`
  flex: 1;
  text-align: right;
  margin-left: 16px;
`

const ProductName = styled.div`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.4;
`

const ProductDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: oklch(55.44% 0.041 257.42);
  font-size: 13px;
`

const ProductQuantity = styled.span`
  color: oklch(55.44% 0.041 257.42);
`

const ProductPrice = styled.div`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  direction: ltr;
`

const RiyalIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: inherit;
`

const SummaryBox = styled.div`
  background: transparent;
  border: none;
  padding: 0;
`

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
`

const SummaryLabel = styled.span`
  color: oklch(55.44% 0.041 257.42);
  font-size: 15px;
  font-weight: 500;
`

const SummaryValue = styled.span`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 16px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  direction: ltr;
`

const FreeShipping = styled.span`
  color: oklch(69.59% 0.149 162.48);
  font-size: 16px;
  font-weight: 700;
`

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0 0 0;
  margin-top: 12px;
  border-top: 2px solid oklch(0.9288 0.0126 255.51);
`

const TotalLabel = styled.span`
  color: oklch(0.2077 0.0398 265.75);
  font-size: 18px;
  font-weight: 800;
`

const TotalValue = styled.span`
  color: oklch(69.59% 0.149 162.48);
  font-size: 24px;
  font-weight: 900;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  direction: ltr;
`

export default function SuccessPage() {
  const navigate = useNavigate()
  const { token } = useParams<{ token: string }>()
  const { order, paymentResult, isProcessing, resetPayment, clearOrder, checkPaymentStatus } = usePaymentStore()
  const { clearCart, items: cartItems } = useCartStore()
  const hasTrackedPurchase = useRef(false)
  const processedOrderNumber = useRef<string | null>(null)

  // ✅ التحقق من أن حدث الشراء لرقم الطلب نفسه لم يُرسل مسبقاً
  const isOrderAlreadyTracked = (orderNumber: string): boolean => {
    if (!orderNumber) return false

    if (processedOrderNumber.current === orderNumber) {
      return true
    }

    try {
      const trackedPurchases = safeLocal.getItem('tracked_purchases')
      if (trackedPurchases) {
        const parsed = JSON.parse(trackedPurchases)
        const now = Date.now()
        const validPurchases = Object.fromEntries(
          Object.entries(parsed).filter(([_, timestamp]) => now - (timestamp as number) < 24 * 60 * 60 * 1000)
        )
        safeLocal.setItem('tracked_purchases', JSON.stringify(validPurchases))

        if (validPurchases[orderNumber]) {
          return true
        }
      }
    } catch {
      // تجاهل أخطاء التخزين
    }

    return false
  }

  const markOrderPurchaseTracked = (orderNumber: string) => {
    if (!orderNumber) return

    processedOrderNumber.current = orderNumber
    hasTrackedPurchase.current = true

    try {
      const trackedPurchases = safeLocal.getItem('tracked_purchases')
      const parsed = trackedPurchases ? JSON.parse(trackedPurchases) : {}
      parsed[orderNumber] = Date.now()
      safeLocal.setItem('tracked_purchases', JSON.stringify(parsed))
    } catch {
      // تجاهل أخطاء التخزين
    }
  }

  // ✅ إرسال Purchase: معرّف المعاملة في المنصات (transaction_id) = رقم الطلب فقط
  const trackPurchaseEvent = async (currentPaymentResult: any) => {
    const orderNumber = resolveOrderNumberFromPaymentData(currentPaymentResult?.data)
    if (!orderNumber) {
      return false
    }

    if (hasTrackedPurchase.current || isOrderAlreadyTracked(orderNumber)) {
      return false
    }

    const persistedDevice = loadPersistedPurchaseDeviceContext()
    if (persistedDevice) {
      eventHub.setPurchaseDeviceOverride(persistedDevice)
    }

    // ✅ استخدام المبلغ الفعلي من paymentResult.data.amount (يشمل المنتجات + الشحن - الخصم)
    const paidAmount = currentPaymentResult?.data?.amount ?? 
                      currentPaymentResult?.data?.transaction?.amount
    const totalValue = paidAmount != null && paidAmount > 0
      ? Number(paidAmount)
      : (Number(order?.amount) || 0) + (Number(order?.shipping) || 0)
    
    if (!totalValue || totalValue <= 0) {
      // لا نرسل الحدث بدون قيمة صالحة
      clearCart()
      return false
    }
    
    // استخدام جميع المنتجات من order.items أو paymentResult.data.items
    const itemsSource = currentPaymentResult?.data?.items || order?.items
    const purchaseItems = itemsSource && itemsSource.length > 0
      ? itemsSource.map((item: any) => ({
          item_id: (item.product_id || item.id || '').toString(),
          item_name: item.product_name || item.title,
          price: item.price,
          quantity: item.quantity || 1,
          item_category: item.category || 'Sweets'
        }))
      : (order?.product ? [{
          item_id: order?.product?.title?.replace(/\s+/g, '-').toLowerCase() || 'product-1',
          item_name: order.product.title,
          price: totalValue,
          quantity: order.qty || 1,
          item_category: 'Sweets'
        }] : [])

    // حجز متزامن محلي لمنع سباق بين effect مزدوج أو استدعاءات متوازية على الجهاز نفسه
    markOrderPurchaseTracked(orderNumber)

    // ✅ حجز على مستوى الخادم: منع تكرار حدث الشراء عند فتح رابط النجاح من جهاز/متصفح آخر.
    // نطلق الحدث فقط إذا أكّد الخادم أن هذا المتصفح فاز بالحجز (أو تعذّر التأكد — fail-open).
    const chargeIdForClaim =
      currentPaymentResult?.data?.id || currentPaymentResult?.data?.charge_id || ''
    const shouldFire = await claimPurchaseOnServer(chargeIdForClaim)
    if (!shouldFire) {
      // الخادم أكّد أن الحدث أُطلق مسبقاً لهذا الطلب — لا نكرّره
      eventHub.clearPurchaseDeviceOverride()
      clearCart()
      return false
    }

    await reidentifyFromCheckoutStorage()

    // transaction_id: اسم الحقل الموحّد لـ Meta/GA4/الهب؛ القيمة دائماً رقم الطلب
    try {
      await eventHub.purchase({
        transaction_id: orderNumber,
        value: totalValue,
        currency: 'SAR',
        items: purchaseItems
      })
    } finally {
      eventHub.clearPurchaseDeviceOverride()
    }

    clearCart()
    return true
  }

  useEffect(() => {
    void (async () => {
      // المسار /success/<token>: نحلّ التوكن إلى charge_id + سياق التتبع من قاعدة البيانات.
      // مهم لرحلة 3DS حيث قد يضيع localStorage عبر النطاقات (متصفحات داخلية).
      if (token) {
        try {
          const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://app.teen-sa.com'
          const r = await fetch(`${BACKEND_URL}/api/public/order-track/${encodeURIComponent(token)}`)
          const j = await r.json()
          if (j.success && j.data) {
            const d = j.data as Record<string, unknown>
            if (typeof d.anon_id === 'string' && d.anon_id) {
              safeLocal.setItem('eh_anon_id', d.anon_id)
            }
            if (d.user && typeof d.user === 'object') {
              safeLocal.setItem('eh_user', JSON.stringify(d.user))
            }
            if (d.first_touch && typeof d.first_touch === 'object') {
              // الدمج بدل الاستبدال: نملأ المفاتيح الناقصة فقط حتى لا نمسح إشارة مُلتقطة محلياً
              // (مثل initial_referrer = سناب) إذا كانت قيمة الخادم ناقصة لطلب قديم.
              try {
                const existingRaw = safeLocal.getItem(FIRST_TOUCH_STORAGE_KEY)
                const existing = existingRaw ? (JSON.parse(existingRaw) as Record<string, unknown>) : {}
                const merged: Record<string, unknown> = { ...(d.first_touch as Record<string, unknown>) }
                for (const [k, v] of Object.entries(existing)) {
                  if ((merged[k] == null || merged[k] === '') && v != null && v !== '') {
                    merged[k] = v
                  }
                }
                safeLocal.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(merged))
              } catch {
                safeLocal.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(d.first_touch))
              }
            }
            const sealedDevice =
              deviceContextFromUnknown(d.client_context) ?? loadPersistedPurchaseDeviceContext()
            if (sealedDevice) {
              eventHub.setPurchaseDeviceOverride(sealedDevice)
            }
            eventHub.hydrateFromLocalStorage()

            const chargeId = typeof d.charge_id === 'string' ? d.charge_id : ''
            if (chargeId) {
              checkPaymentStatus(chargeId)
              return
            }
          }
        } catch {
          /* noop — نسقط للاعتماد على الحالة المحفوظة */
        }
      }

      // احتياط: لا توكن (أو فشل حلّه) — اعتمد على حالة الدفع المحفوظة محلياً.
      const currentPaymentResult = paymentResult

      if (currentPaymentResult?.data) {
        const cid = currentPaymentResult.data.id || currentPaymentResult.data.charge_id || ''
        if (cid) {
          checkPaymentStatus(cid)
          return
        }
      }

      if (currentPaymentResult?.success) {
        void trackPurchaseEvent(currentPaymentResult).catch(() => {})
        return
      }

      navigate('/', { replace: true })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ✅ الاستماع لتحديثات paymentResult من checkPaymentStatus
  // نتحقق من isProcessing لتجنب التوجيه المبكر بسبب بيانات التخزين المحلي القديمة
  useEffect(() => {
    if (isProcessing) return // لا نتخذ قراراً بينما checkPaymentStatus لا تزال تعمل

    if (paymentResult?.success) {
      void trackPurchaseEvent(paymentResult).catch(() => {})
    } else if (paymentResult && !paymentResult.success) {
      // الدفع فاشل وتأكدنا من الباك إند — نوجّه للفشل
      navigate('/failure', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentResult?.success, isProcessing])

  const backToStore = () => {
    clearCart()
    resetPayment()
    clearOrder()
    navigate('/', { replace: true })
  }

  // ✅ استخدام items من الباك اند أولاً (paymentResult.data.items)، ثم order.items، ثم cartItems كحل احتياطي
  let orderItems: Array<{
    id: string
    title: string
    price: number
    quantity: number
    image?: string
    description?: string
    category?: string // ✅ إضافة category للتتبع
  }> = []
  
  // الأولوية: items من استجابة الباك اند
  if (paymentResult?.data?.items && Array.isArray(paymentResult.data.items) && paymentResult.data.items.length > 0) {
    orderItems = paymentResult.data.items.map((item: any) => ({
      id: (item.product_id || item.id || '').toString(), // ✅ تحويل إلى string
      title: item.product_name || item.title || '',
      price: item.price || 0,
      quantity: item.quantity || 1,
      image: item.image,
      description: item.description,
      category: item.category || 'Sweets' // ✅ إضافة category
    }))
  } else if (order?.items && order.items.length > 0) {
    // الثاني: order.items من الحالة المحلية
    orderItems = order.items.map(item => ({
      ...item,
      id: item.id.toString(), // ✅ تحويل إلى string
      category: (item as any).category || 'Sweets' // ✅ إضافة category
    }))
  } else if (cartItems && cartItems.length > 0) {
    // الثالث: cartItems كحل احتياطي
    orderItems = cartItems.map(item => ({
      id: item.id.toString(), // ✅ تحويل إلى string
      title: item.title,
      price: item.price,
      quantity: item.quantity,
      image: item.image,
      description: item.description,
      category: item.category || 'Sweets' // ✅ إضافة category
    }))
  }

  // ✅ استخدام المبلغ الفعلي من استجابة الباك إند (بعد الخصم)
  // الأولوية: paymentResult.data.amount (المبلغ الفعلي الذي تم دفعه)
  // الثاني: paymentResult.data.transaction.amount
  // الثالث: order.amount (حل احتياطي)
  const paidAmount = paymentResult?.data?.amount || 
                     paymentResult?.data?.transaction?.amount || 
                     order?.amount || 
                     0

  // ✅ حساب قيمة الطلب (قبل الخصم)
  // الأولوية: من paymentResult.data.cart_total (من الباك إند)
  // الثاني: من جمع items
  // الثالث: من order.amount (حل احتياطي)
  const cartTotal = paymentResult?.data?.cart_total || 
                   (orderItems.length > 0
                     ? orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
                     : (order?.amount || 0))

  // ✅ حساب الشحن
  // الأولوية: من paymentResult.data.shipping (من الباك إند)
  // الثاني: من order.shipping
  const shipping = paymentResult?.data?.shipping || order?.shipping || 0

  // ✅ حساب الخصم
  // الأولوية: من paymentResult.data.discount (من الباك إند)
  // الثاني: من الفرق بين قيمة الطلب + الشحن والمبلغ المدفوع
  const calculatedTotal = cartTotal + shipping
  const discount = paymentResult?.data?.discount || 
                  (calculatedTotal > paidAmount ? (calculatedTotal - paidAmount) : 0)

  // ✅ المبلغ الإجمالي المعروض = المبلغ الفعلي الذي تم دفعه
  const total = paidAmount
  
  const totalQuantity = orderItems.length > 0 
    ? orderItems.reduce((sum, item) => sum + item.quantity, 0)
    : (order?.qty || 1)
  
  const orderNumber =
    resolveOrderNumberFromPaymentData(paymentResult?.data) || '—'

  const cleanMessage = paymentResult?.message?.replace(/✅|❌|⚠️|💰|🚫|💚|ℹ️|✋/g, '').trim() || 'تم الدفع بنجاح'

  return (
    <S.PageContainer>
      <S.Wrapper>
        <CardWithoutShadow>
          <S.CardBody>
            <HeroWithLessPadding>
              <PartyPopper size={64} style={{ color: 'oklch(69.59% 0.149 162.48)' }} />
              <S.Title>تم إتمام الشراء بنجاح</S.Title>
              <S.Subtitle>{cleanMessage}</S.Subtitle>
              <OrderNumberBox>
                <OrderNumberText>رقم الطلب</OrderNumberText>
                <OrderNumberValue>{orderNumber}</OrderNumberValue>
              </OrderNumberBox>
            </HeroWithLessPadding>

            <S.Summary>
              {/* المنتجات */}
              {orderItems.length > 0 && (
                <ProductsContainer>
                  {orderItems.map((item) => (
                    <ProductRow key={item.id}>
                      <ProductInfo>
                        <ProductName>{item.title}</ProductName>
                        <ProductDetails>
                          <ProductQuantity>الكمية: {item.quantity}</ProductQuantity>
                        </ProductDetails>
                      </ProductInfo>
                      <ProductPrice>
                        <RiyalIcon>
                          <SaudiRiyal size={16} strokeWidth={2.5} />
                        </RiyalIcon>
                        {(item.price * item.quantity).toFixed(2)}
                      </ProductPrice>
                    </ProductRow>
                  ))}
                </ProductsContainer>
              )}

              {/* للتوافق مع الكود القديم */}
              {orderItems.length === 0 && order?.product?.title && (
                <ProductsContainer>
                  <ProductRow>
                    <ProductInfo>
                      <ProductName>{order.product.title}</ProductName>
                      <ProductDetails>
                        <ProductQuantity>الكمية: {totalQuantity}</ProductQuantity>
                      </ProductDetails>
                    </ProductInfo>
                    <ProductPrice>
                      <RiyalIcon>
                        <SaudiRiyal size={16} strokeWidth={2.5} />
                      </RiyalIcon>
                      {cartTotal.toFixed(2)}
                    </ProductPrice>
                  </ProductRow>
                </ProductsContainer>
              )}

              {/* الملخص المالي */}
              <SummaryBox>
                {cartTotal > 0 && (
                  <SummaryItem>
                    <SummaryLabel>قيمة الطلب</SummaryLabel>
                    <SummaryValue>
                      <RiyalIcon>
                        <SaudiRiyal size={16} strokeWidth={2.5} />
                      </RiyalIcon>
                      {cartTotal.toFixed(2)}
                    </SummaryValue>
                  </SummaryItem>
                )}
                <SummaryItem>
                  <SummaryLabel>رسوم التوصيل</SummaryLabel>
                  {shipping > 0 ? (
                    <SummaryValue>
                      <RiyalIcon>
                        <SaudiRiyal size={16} strokeWidth={2.5} />
                      </RiyalIcon>
                      {shipping.toFixed(2)}
                    </SummaryValue>
                  ) : (
                    <FreeShipping>مجاني</FreeShipping>
                  )}
                </SummaryItem>
                {discount > 0 && (
                  <SummaryItem>
                    <SummaryLabel>الخصم</SummaryLabel>
                    <SummaryValue style={{ color: 'oklch(69.59% 0.149 162.48)' }}>
                      <RiyalIcon>
                        <SaudiRiyal size={16} strokeWidth={2.5} />
                      </RiyalIcon>
                      -{discount.toFixed(2)}
                    </SummaryValue>
                  </SummaryItem>
                )}
                <TotalRow>
                  <TotalLabel>المبلغ المدفوع</TotalLabel>
                  <TotalValue>
                    <RiyalIcon>
                      <SaudiRiyal size={18} strokeWidth={3} />
                    </RiyalIcon>
                    {total > 0 ? total.toFixed(2) : '0.00'}
                  </TotalValue>
                </TotalRow>
              </SummaryBox>
            </S.Summary>

            <S.Actions>
              <S.PrimaryButton onClick={backToStore}>
                العودة للمتجر
              </S.PrimaryButton>
            </S.Actions>
          </S.CardBody>
        </CardWithoutShadow>
      </S.Wrapper>
    </S.PageContainer>
  )
}
